import React, { useMemo, useState } from "react";
import { api, apiErr } from "@/foundation/api/client";
import { toast } from "sonner";
import { useAuth } from "@/foundation/auth/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SuiteButton, SuiteEyebrow } from "@/components/suite";
import { Eye, EyeOff, ShieldCheck, CheckCircle2, XCircle, KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Regole di forza password — devono restare in SYNC con
 * `validate_password_strength` in /app/backend/auth.py.
 */
const RULES = [
  { key: "len",   label: "Almeno 8 caratteri",              test: (v) => v.length >= 8 },
  { key: "upper", label: "Una lettera maiuscola (A-Z)",     test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "Una lettera minuscola (a-z)",     test: (v) => /[a-z]/.test(v) },
  { key: "digit", label: "Un numero (0-9)",                 test: (v) => /\d/.test(v) },
  { key: "sym",   label: "Un simbolo (! @ # $ ? …)",        test: (v) => /[^A-Za-z0-9]/.test(v) },
];

function StrengthList({ pwd }) {
  return (
    <ul className="space-y-1" data-testid="pwd-rules">
      {RULES.map((r) => {
        const ok = r.test(pwd);
        return (
          <li key={r.key} className={`flex items-center gap-2 text-xs ${ok ? "text-suite-accent-hover" : "text-suite-neutral-500"}`}
              data-testid={`pwd-rule-${r.key}-${ok ? "ok" : "ko"}`}>
            {ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
            {r.label}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * ChangePasswordDialog — self-service cambio password.
 * Alla conferma il backend risponde con `logout: true` e cancella i cookie:
 * eseguiamo `logout()` del context e reindirizziamo al /login.
 */
export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const allRulesOk = useMemo(() => RULES.every((r) => r.test(newPwd)), [newPwd]);
  const match = newPwd.length > 0 && newPwd === confirmPwd;
  const canSubmit = currentPwd && allRulesOk && match && !loading && newPwd !== currentPwd;

  const reset = () => {
    setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); setShowNew(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success("Password aggiornata — per sicurezza devi effettuare nuovamente l'accesso");
      reset();
      onOpenChange(false);
      // Il backend ha già cancellato i cookie; aggiorno lo stato del context e reindirizzo.
      setTimeout(async () => {
        await logout();
        navigate("/login");
      }, 300);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!loading) { if (!o) reset(); onOpenChange(o); } }}>
      <DialogContent formDialog className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <SuiteEyebrow tone="primary" icon={ShieldCheck}>Sicurezza account</SuiteEyebrow>
          <DialogTitle className="mt-1" style={{ fontFamily: "Manrope" }}>
            Cambia la mia password
          </DialogTitle>
          <DialogDescription>
            Al termine dovrai effettuare nuovamente l&apos;accesso con la nuova password.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cp-current" className="text-xs uppercase tracking-widest text-suite-neutral-500">
              Password attuale
            </Label>
            <Input
              id="cp-current" type="password" autoComplete="current-password" required
              value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)}
              data-testid="cp-current-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cp-new" className="text-xs uppercase tracking-widest text-suite-neutral-500">
              Nuova password
            </Label>
            <div className="relative">
              <Input
                id="cp-new" type={showNew ? "text" : "password"} autoComplete="new-password" required
                value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                className="pr-10"
                data-testid="cp-new-input"
              />
              <button type="button" onClick={() => setShowNew((v) => !v)}
                      className="absolute right-2 top-2 p-1 text-suite-neutral-400 hover:text-suite-neutral-700"
                      tabIndex={-1} data-testid="cp-toggle-visibility">
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="pt-1 pl-1"><StrengthList pwd={newPwd} /></div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cp-confirm" className="text-xs uppercase tracking-widest text-suite-neutral-500">
              Conferma nuova password
            </Label>
            <Input
              id="cp-confirm" type={showNew ? "text" : "password"} autoComplete="new-password" required
              value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
              data-testid="cp-confirm-input"
            />
            {confirmPwd.length > 0 && !match && (
              <div className="text-xs text-suite-error" data-testid="cp-mismatch">
                Le due password non coincidono
              </div>
            )}
            {newPwd && newPwd === currentPwd && (
              <div className="text-xs text-suite-error" data-testid="cp-same-as-current">
                Deve essere diversa dalla password attuale
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <SuiteButton variant="secondary" onClick={() => onOpenChange(false)} disabled={loading} data-testid="cp-cancel-btn">
              Annulla
            </SuiteButton>
            <SuiteButton
              type="submit"
              variant="primary"
              icon={KeyRound}
              loading={loading}
              disabled={!canSubmit}
              data-testid="cp-submit-btn"
            >
              Aggiorna password
            </SuiteButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
